import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  protocolApplicationsApi,
  protocolsApi,
  type Protocol,
  type ProtocolApplication,
} from '../../lib/api.js';

/// O previsto de um produto, já somado entre as etapas: o frasco sai do estoque uma vez só.
export interface ProtocolPlannedMaterial {
  productId: string;
  quantity: string;
}

export interface ClosingProtocol {
  loading: boolean;
  error: unknown;
  application: ProtocolApplication | null;
  /// O protocolo escolhido para este atendimento: o aplicado, ou o planejado enquanto não aplicou.
  chosenId: string | null;
  protocols: readonly Protocol[];
  /// O previsto do escolhido. Nulo sem protocolo ou sem permissão de ver materiais.
  planned: ProtocolPlannedMaterial[] | null;
  applied: boolean;
  /// Grava a escolha e devolve o previsto do escolhido, para o material se refazer com ele.
  choose: (protocolId: string | null) => Promise<ProtocolPlannedMaterial[] | null>;
  /// Aplica o escolhido, se ainda não foi aplicado. Aplicar é irreversível (ADR 0029).
  applyChosen: () => Promise<void>;
}

const applicationsKey = ['company', 'protocol-applications'] as const;

/// O protocolo do fechamento (ADR 0030). Escolher grava o planejado na hora — é o rascunho do
/// protocolo —, e aplicar acontece quando o atendimento é concluído.
export function useClosingProtocol({
  appointmentId,
  serviceId,
  enabled,
  canReadMaterials,
}: {
  appointmentId: string;
  serviceId: string | undefined;
  enabled: boolean;
  canReadMaterials: boolean;
}): ClosingProtocol {
  const queryClient = useQueryClient();
  const application = useQuery({
    queryKey: [...applicationsKey, 'by-appointment', appointmentId],
    queryFn: () => protocolApplicationsApi.findByAppointment(appointmentId),
    enabled,
    retry: false,
  });
  const protocols = useQuery({
    queryKey: ['company', 'protocols', 'closing-options', serviceId],
    queryFn: () =>
      protocolsApi.list({
        activity: 'ACTIVE',
        service: serviceId ?? 'ALL',
        sort: 'displayName',
        direction: 'asc',
        limit: 100,
        offset: 0,
      }),
    enabled: enabled && Boolean(serviceId),
    retry: false,
  });
  const current = application.data ?? null;
  const chosenId = current?.protocolId ?? null;
  const applied = current?.status === 'APPLIED';
  const materials = useQuery({
    queryKey: ['company', 'protocols', 'materials', chosenId],
    queryFn: () => protocolsApi.getMaterials(chosenId!),
    enabled: enabled && canReadMaterials && Boolean(chosenId) && !applied,
    retry: false,
  });

  /// Aplicado, o previsto é o retrato daquele instante; planejado, é o vigente do protocolo.
  const planned: ProtocolPlannedMaterial[] | null = !chosenId
    ? null
    : applied
      ? sumByProduct(current.steps.flatMap((step) => step.materials))
      : canReadMaterials && materials.data
        ? sumByProduct(materials.data)
        : null;

  async function refresh() {
    await queryClient.invalidateQueries({ queryKey: applicationsKey });
  }

  return {
    loading: application.isLoading || protocols.isLoading,
    error: application.error ?? protocols.error,
    application: current,
    chosenId,
    protocols: protocols.data?.protocols ?? [],
    planned,
    applied,
    choose: async (protocolId) => {
      if (protocolId) {
        await protocolApplicationsApi.plan({ appointmentId, protocolId });
      } else if (current && current.status === 'PLANNED') {
        await protocolApplicationsApi.removePlanned(current.id);
      }
      await refresh();
      if (!protocolId || !canReadMaterials) return null;
      const next = await queryClient.fetchQuery({
        queryKey: ['company', 'protocols', 'materials', protocolId],
        queryFn: () => protocolsApi.getMaterials(protocolId),
      });
      return sumByProduct(next);
    },
    applyChosen: async () => {
      if (!chosenId || applied) return;
      await protocolApplicationsApi.apply({ appointmentId, protocolId: chosenId });
      await refresh();
    },
  };
}

/// Soma em inteiro escalado a seis casas, como a API: somar em ponto flutuante devolveria
/// `0.30000000000000004` como previsão.
function sumByProduct(
  materials: ReadonlyArray<{ productId: string; quantity: string }>,
): ProtocolPlannedMaterial[] {
  const totals = new Map<string, bigint>();
  for (const material of materials) {
    totals.set(
      material.productId,
      (totals.get(material.productId) ?? 0n) + scaled(material.quantity),
    );
  }
  return [...totals.entries()].map(([productId, total]) => ({
    productId,
    quantity: unscaled(total),
  }));
}

function scaled(value: string): bigint {
  const [whole = '0', fraction = ''] = value.split('.');
  return BigInt(whole) * 1_000_000n + BigInt(fraction.padEnd(6, '0').slice(0, 6));
}

function unscaled(value: bigint): string {
  const whole = value / 1_000_000n;
  const fraction = (value % 1_000_000n).toString().padStart(6, '0').replace(/0+$/u, '');
  return fraction ? `${whole.toString()}.${fraction}` : whole.toString();
}
